import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// Types
import { FormInputs } from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/CreateStreamSetup';
// Hooks
import { useStreamEarnings } from 'hooks/useEstimatedStreamProfit';
import { useGetPpvTiers } from 'hooks/useGetPpvTiers';
import { useTicketingLaunched } from 'hooks/useTicketingLaunched';
// Helpers
import { getPlatformFeeCopy, getPlatformFeePercent } from 'helpers/platformFee';
import { formatPriceToUSD } from 'helpers/priceText';
// Ui
import Switch from 'ui3/Switch/Switch';
import Text from 'ui3/Text/Text';
// Components
import PriceTierSelector from 'components/common3/PriceTierSelector/PriceTierSelector';
import SectionDivider from 'components/common3/SectionDivider/SectionDivider';
import StreamOptionRow from 'components/common3/StreamOptionRow/StreamOptionRow';
import InfoTooltip from 'components/ManageAma/Campaigns/common/InfoTooltip/InfoTooltip';
import AffiliateProgramSection from 'components/ProfileSetup/components/common/AffiliateProgramSection/AffiliateProgramSection';
import ToggleSection from 'components/ManageEvents/CreateEventSetup/PricingTicketsStep/ToggleSection/ToggleSection';
import WizardStepLayout from 'components/ProfileSetup/components/common/CreateStreamWizard/WizardStepLayout/WizardStepLayout';
import StreamSummaryPanel from 'components/ProfileSetup/components/common/CreateStreamWizard/StreamSummaryPanel/StreamSummaryPanel';
import StreamTicketsFields from './StreamTicketsFields/StreamTicketsFields';
// Styles
import styles from './PricingStep.module.scss';

const PricingStep = (): JSX.Element => {
  const {
    control,
    register,
    errors,
    watch,
    trigger,
    setValue,
  } = useFormContext<FormInputs>();
  const {
    tiers: ppvTiers,
    loading: ppvTiersLoading,
    error: ppvTiersError,
    refetch: refetchPpvTiers,
  } = useGetPpvTiers();

  const isFreeStream = Boolean(watch('isFree'));
  const price = Number(watch('price'));
  const { netProfit, youEarn } = useStreamEarnings({
    price,
    isFree: isFreeStream,
    affiliateEnabled: watch('affiliateEnabled'),
    affiliateCommissionPercent: watch('affiliateCommissionPercent'),
  });
  const showEarnings = price > 0 && netProfit > 0;
  const ticketsEnabled = watch('ticketsEnabled');
  const ticketingLaunched = useTicketingLaunched();

  // Only a paid stream sells venue tickets alongside it.
  useEffect(() => {
    if (isFreeStream) {
      setValue('ticketsEnabled', false);
    }
  }, [isFreeStream, setValue]);

  return (
    <WizardStepLayout aside={<StreamSummaryPanel />}>
      <div className={styles.heading}>
        <Text variant="h4">Pricing & Access</Text>
        <Text variant="body1Regular16" color="lights-low">
          Choose what fans pay to watch this PPV event.
        </Text>
      </div>

      <div className={styles.pricing}>
        <StreamOptionRow
          className={styles.freeCard}
          title="Make this Stream Free"
          description="Run this stream as free for fans."
        >
          <Switch
            wrapperClassName={styles.switch}
            name="isFree"
            ref={register}
          />
        </StreamOptionRow>

        {!isFreeStream && (
          <div className={styles.priceBlock}>
            <Text
              variant="body2Medium"
              color="lights-low"
              className={styles.priceLabel}
            >
              Stream price
            </Text>
            <div className={styles.priceField}>
              <Controller
                control={control}
                name="price"
                defaultValue=""
                render={({ onChange, value }) => (
                  <PriceTierSelector
                    tiers={ppvTiers}
                    value={value ?? ''}
                    onSelect={(selected) => {
                      onChange(String(selected));
                      trigger('price');
                    }}
                    loading={ppvTiersLoading}
                    error={ppvTiersError}
                    onRetry={() => refetchPpvTiers()}
                  />
                )}
              />
              {errors?.price?.message && (
                <Text variant="captionRegular" color="error-default">
                  {errors.price.message}
                </Text>
              )}
              {showEarnings && (
                <div className={styles.priceNotice}>
                  <InfoTooltip
                    title="Platform Fee"
                    text={getPlatformFeeCopy(
                      getPlatformFeePercent(price, netProfit)
                    )}
                    iconSize={16}
                    place="left"
                  />
                  <Text as="span" variant="captionRegular" color="lights-low">
                    You earn{' '}
                    <Text
                      as="span"
                      variant="body3Medium"
                      color="accent-default"
                    >
                      {formatPriceToUSD(youEarn)}
                    </Text>
                    .
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <SectionDivider />

      <AffiliateProgramSection netProfit={netProfit} disabled={isFreeStream} />

      {ticketingLaunched && (
        <>
          <SectionDivider />

          <ToggleSection
            ref={register}
            name="ticketsEnabled"
            disabled={isFreeStream}
            title="Event Tickets"
            description="Sell in-person tickets alongside the stream. Set the venue and create ticket tiers."
          >
            {ticketsEnabled && !isFreeStream && <StreamTicketsFields />}
          </ToggleSection>
        </>
      )}
    </WizardStepLayout>
  );
};

export default PricingStep;
